/* Aldodiego Web 3.0 — Geoapify/Leaflet delivery picker
 * Theme asset: assets/ad360-delivery-a.js
 * Uses existing section markup and data-maps-key as compatibility bridge.
 * NOTE: Browser pricing is indicative; Shopify must independently enforce rates and coverage.
 */
(() => {
  "use strict";
  const root = document.getElementById("ad-shop");
  if (!root || root.dataset.packageAInitialized) return;
  root.dataset.packageAInitialized = "true";

  const el = id => root.querySelector("#" + id);
  const delivery = el("ad-delivery");
  const input = el("ad-address");
  const suggestions = el("ad-address-suggestions");
  const status = el("ad-location-status");
  const quoteBox = el("ad-delivery-quote");
  const mapBox = el("ad-address-map");
  const confirmButton = el("ad-confirm-location");
  const changeButton = el("ad-change-location");
  const mapMount = el("ad-google-address");
  if (![delivery, input, suggestions, status, quoteBox, mapBox, confirmButton, changeButton].every(Boolean)) return;

  // The current Shopify Liquid section supplies data-maps-key; it can temporarily
  // hold the Geoapify key until its setting is renamed to geoapify_api_key.
  const apiKey = (root.dataset.geoapifyKey || root.dataset.mapsKey || "").trim();
  const googleDemoKey = (root.dataset.googleMapsDemoKey || "").trim();
  const maxKm = Math.max(10, Math.min(20, Number(root.dataset.coverageMaxKm) || 10));
  const checkoutReady = root.dataset.deliveryCheckoutReady === "true";
  const centers = [
    { name: "Larco", position: { lat: -8.1165318, lng: -79.0311109 } },
    { name: "Húsares", position: { lat: -8.1260929, lng: -79.0356240 } },
    { name: "El Golf", position: { lat: -8.1380859, lng: -79.0332633 } }
  ];
  const cityCenter = { lat: -8.11653, lng: -79.03111 };
  // Coarse region check, NOT a substitute for an approved service-area polygon.
  const isInTrujilloRegion = pos => pos.lat >= -8.28 && pos.lat <= -7.96 && pos.lng >= -79.26 && pos.lng <= -78.88;
  const fmtMoney = n => "S/ " + Number(n).toFixed(2).replace(".00", "");
  // Tarifas calculadas con radios equivalentes de Shopify Local Delivery (no distancia vial).
  // Radios estimados: contrastar con direcciones reales antes de abrir el checkout.
  const radiusPricing = [
    { maxMeters: 800, fee: 4, minimum: 10 },
    { maxMeters: 2200, fee: 6, minimum: 10 },
    { maxMeters: 3700, fee: 7, minimum: 10 },
    { maxMeters: 5000, fee: 8, minimum: 10 },
    { maxMeters: 6000, fee: 9, minimum: 10 },
    { maxMeters: 7500, fee: 10, minimum: 10 }
  ];
  // Húsares: copia de los nueve tramos guardados en Shopify por Aldodiego.
  // NO aplicar estas compras mínimas a Larco ni El Golf sin revisarlas.
  const husaresRadiusPricing = [
    { maxMeters: 800, fee: 4, minimum: 10 },
    { maxMeters: 1500, fee: 5, minimum: 10 },
    { maxMeters: 2500, fee: 6, minimum: 10 },
    { maxMeters: 3500, fee: 7, minimum: 10 },
    { maxMeters: 5000, fee: 8, minimum: 10 },
    { maxMeters: 6100, fee: 9, minimum: 10 },
    { maxMeters: 6600, fee: 10, minimum: 10 },
    { maxMeters: 9900, fee: 10, minimum: 10 },
    { maxMeters: 11100, fee: 10, minimum: 10 }
  ];
  const haversine = (a, b) => {
    const r = 6371000, deg = Math.PI / 180;
    const dLat = (b.lat - a.lat) * deg, dLng = (b.lng - a.lng) * deg;
    const x = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * deg) * Math.cos(b.lat * deg) * Math.sin(dLng / 2) ** 2;
    return 2 * r * Math.asin(Math.min(1, Math.sqrt(x)));
  };
  const radialTier = (radiusMeters, origin) => (origin==="Húsares" ? husaresRadiusPricing : radiusPricing).find(t => radiusMeters <= t.maxMeters) || null;

  // Solo activar nuevas zonas remotas cuando Aldodiego apruebe su cobertura.
  const distantAuthorizedZones = ["huanchaco"];
  const isRemoteSpecialZone = place => distantAuthorizedZones.some(name =>
    [place?.address, place?.locality].filter(Boolean).join(" ")
      .normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().includes(name));
  function minimumFor(place, meters, origin) {
    const placeText = [place?.address, place?.locality].filter(Boolean)
      .join(" ").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
    if (distantAuthorizedZones.some(name => placeText.includes(name))) return 10;
    return radialTier(meters,origin)?.minimum ?? 10;
  }
  const minimumLabel = minimum => minimum > 0
    ? "Compra mínima en productos: " + fmtMoney(minimum) + ". "
    : "Sin mínimo de consumo. ";
  const freeDeliveryThresholdCents = 7000;
  const effectiveFeeFor = (subtotalCents, deliveryQuote) =>
    Number.isFinite(subtotalCents) && subtotalCents >= freeDeliveryThresholdCents ? 0 : deliveryQuote.fee;
  const isDelivery = () => !!root.querySelector('[data-method="delivery"].selected');
  const message = (text, bad = false) => {
    quoteBox.textContent = text;
    quoteBox.dataset.error = bad ? "true" : "false";
  };
  const setValidated = value => {
    root.dataset.deliveryValidated = value ? "true" : "false";
    if (!value) root.dataset.orderConfigured = "false";
  };

  let map = null, marker = null, selected = null, quote = null, confirmed = false;
  let checkoutQuoteSequence = 0;
  // La dirección escrita por el cliente manda sobre una calle cercana sugerida
  // por geocodificación inversa; las coordenadas del mapa se conservan.
  const streetConfirmation = document.createElement("label");
  streetConfirmation.className = "ad-field";
  streetConfirmation.id = "ad360-street-confirmation";
  streetConfirmation.textContent = "No reconocimos la calle: escribe aquí la dirección exacta";
  const streetInput = document.createElement("input");
  streetInput.type = "text";
  streetInput.autocomplete = "street-address";
  streetInput.placeholder = "Escribe la calle y número reales de entrega";
  streetInput.setAttribute("aria-label","Confirma o corrige tu calle y número de delivery");
  streetConfirmation.appendChild(streetInput);
  streetConfirmation.style.display = "none";
  streetConfirmation.style.margin = "14px 0 16px";
  // Mostrar la corrección justo bajo el campo de búsqueda, ANTES de confirmar.
  // Anteriormente quedaba después del mapa y el usuario nunca la veía.
  const addressSearchLabel=input.closest("label");
  if(!addressSearchLabel)throw new Error("No está disponible el formulario de dirección.");
  addressSearchLabel.insertAdjacentElement("afterend",streetConfirmation);
  streetInput.addEventListener("input",()=>{
    if(!selected)return;
    if(confirmed){
      confirmed=false;
      setValidated(false);
      confirmButton.hidden=false;
      renderCartNote();
    }
  });
  let run = 0, searchSequence = 0, searchTimer = null, searchAbort = null;
  let leafletPromise = null, googlePromise = null, mapEngine = null;

  function hideSuggestions() {
    suggestions.hidden = true;
    suggestions.replaceChildren();
  }

  function renderCartNote() {
    const quoteSequence = ++checkoutQuoteSequence;
    const note = el("ad-checkout-shipping-note");
    if (!note) return;
    const shippingLabel = el("ad-cart-shipping-label");
    const totalLabel = el("ad-cart-total-label");
    if (shippingLabel) shippingLabel.textContent = isDelivery() ? "Delivery (estimado)" : "Retiro en tienda";
    if (totalLabel) totalLabel.textContent = isDelivery() ? "Total estimado" : "Subtotal de productos";
    if (!isDelivery()) {
      const store = root.querySelector('input[name="ad-store"]:checked')?.value || "Larco";
      note.textContent = "Retiro en " + store + " · GRATIS. Abriremos el pago con esa tienda preseleccionada; verifica Retiro antes de pagar. Shopify permite cambiar manualmente a Envío.";
    } else if (confirmed && quote) {
      const currentQuote = quote;
      note.textContent = "Delivery normal: " + fmtMoney(quote.fee) + ". Gratis desde S/ 70 en productos, dentro de las zonas urbanas. " + minimumLabel(quote.minimum) + "El checkout de Shopify determina el importe final.";
      void fetch("/cart.js", { credentials: "same-origin" }).then(response => {
        if (!response.ok) throw new Error("Carrito no disponible");
        return response.json();
      }).then(cart => {
        if (!isDelivery() || quote !== currentQuote || !confirmed || quoteSequence !== checkoutQuoteSequence) return;
        // Tomar el subtotal que ya muestra el carrito: una respuesta Ajax vieja
        // o un subtotal de 0 provocaba mensajes como "faltan S/ 70" con S/ 16.
        const visibleText=String(el("ad-cart-subtotal")?.textContent||"").replace(/[^0-9.,-]/g,"");
        const visiblePrice=Number(visibleText.replace(/,/g,""));
        const visibleCents=Number.isFinite(visiblePrice)?Math.round(visiblePrice*100):0;
        const subtotal=visibleCents>0?visibleCents:Math.max(Number(cart.items_subtotal_price)||0,Number(cart.total_price)||0);
        if(!Number.isFinite(subtotal))return;
        const effectiveFee = effectiveFeeFor(subtotal, currentQuote);
        const promotion = effectiveFee === 0
          ? "Delivery gratis estimado por compra de S/ 70 o más."
          : "Delivery estimado: " + fmtMoney(effectiveFee) + ". Te faltan " + fmtMoney(Math.max(0, (freeDeliveryThresholdCents - subtotal) / 100)) + " en productos para delivery gratis.";
        note.textContent = promotion + " " + minimumLabel(currentQuote.minimum) + "Importe final sujeto a las condiciones del checkout Shopify.";
        if(!root.querySelector('[data-screen="cart"]')?.classList.contains("is-active") || !selected?.streetAddress || !cart.items?.length)return;
        const shippingDisplay=el("ad-cart-shipping"),totalDisplay=el("ad-cart-total");
        if(shippingDisplay)shippingDisplay.textContent="Consultando Shopify…";
        if(totalDisplay)totalDisplay.textContent="Por confirmar";
        const placeAtRequest=selected;
        void ad360FetchLocalDeliveryRate(cart,placeAtRequest).then(localFeeCents=>{
          if(quoteSequence!==checkoutQuoteSequence||!confirmed||!isDelivery()||selected!==placeAtRequest)return;
          if(localFeeCents===null){
            if(shippingDisplay)shippingDisplay.textContent="Por confirmar";
            if(totalDisplay)totalDisplay.textContent="Por confirmar";
            note.textContent="Shopify no ofrece una tarifa verificable para esta dirección y el pedido actual. Puede faltar el consumo mínimo de la zona o una calle/número válido. Corrige la dirección o agrega productos antes de pagar.";
            return;
          }
          if(shippingDisplay)shippingDisplay.textContent=fmtMoney(localFeeCents/100);
          const productCents=Number(cart.total_price);
          if(totalDisplay)totalDisplay.textContent=Number.isFinite(productCents)?fmtMoney((productCents+localFeeCents)/100):"Por confirmar";
          const difference=localFeeCents-Math.round(effectiveFee*100);
          note.textContent="Entrega local según Shopify: "+fmtMoney(localFeeCents/100)+
            (difference!==0?". La tarifa es distinta del cálculo por distancia ("+fmtMoney(effectiveFee)+").":"")+
            " "+(subtotal<freeDeliveryThresholdCents?"Te faltan "+fmtMoney((freeDeliveryThresholdCents-subtotal)/100)+" en productos para el umbral promocional. ":"")+
            "El importe definitivo se confirma en el checkout.";
        });
      }).catch(() => {});

    } else {
      note.textContent = "Confirma tu dirección exacta y la cobertura antes de continuar con delivery.";
    }
  }

  function invalidate({ clearAddress = false, hideMap = false } = {}) {
    ++run;
    selected = null;
    quote = null;
    delete root.dataset.deliveryFee;
    confirmed = false;
    setValidated(false);
    confirmButton.hidden = true;
    changeButton.hidden = true;
    if (clearAddress) {
      input.value = "";
      streetInput.value = "";
      streetConfirmation.style.display = "none";
    }
    if (hideMap) mapBox.hidden = true;
    renderCartNote();
  }

  async function geoapify(path, parameters = {}, signal) {
    if (!apiKey) throw new Error("Falta configurar Geoapify");
    const url = new URL("https://api.geoapify.com/v1/" + path);
    for (const [name, value] of Object.entries(parameters)) url.searchParams.set(name, String(value));
    url.searchParams.set("apiKey", apiKey);
    const response = await fetch(url.toString(), { signal, mode: "cors" });
    if (!response.ok) throw new Error("Geoapify respondió " + response.status);
    return response.json();
  }

  function normalizeResult(row, exactGpsPosition) {
    if (!row || !Number.isFinite(Number(row.lat)) || !Number.isFinite(Number(row.lon))) return null;
    const street = String(row.street || row.address_line1 || "").trim();
    const number = String(row.housenumber || "").trim();
    const district = String(row.suburb || row.neighbourhood || row.district || row.city_district || "").trim();
    const city = String(row.city || row.town || row.village || row.municipality || "Trujillo").trim();
    const localityParts = [district, city, row.state].filter(Boolean);
    // Prefer a street address over a nearby named place (for example, a school).
    const streetAddress = [number, street].filter(Boolean).join(" ");
    const address = streetAddress
      ? [streetAddress, ...localityParts].filter(Boolean).join(", ")
      : String(row.formatted || row.address_line1 || "").trim();
    return {
      address,
      streetAddress: streetAddress || String(row.address_line1 || row.formatted || "").trim(),
      district,
      city,
      postcode: String(row.postcode || "").trim(),
      pos: exactGpsPosition || { lat: Number(row.lat), lng: Number(row.lon) },
      country: String(row.country_code || "").toLowerCase(),
      locality: [row.city, row.county, row.district, row.suburb, row.state, row.address_line2].filter(Boolean).join(" ")
    };
  }

  // Load Leaflet once; all files stay in the existing JavaScript asset.
  function ensureLeaflet() {
    if (window.L?.map) return Promise.resolve(window.L);
    if (leafletPromise) return leafletPromise;
    leafletPromise = new Promise((resolve, reject) => {
      const css = document.createElement("link");
      css.rel = "stylesheet";
      css.href = "https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/leaflet.css";
      document.head.appendChild(css);
      const script = document.createElement("script");
      script.src = "https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/leaflet.js";
      script.async = true;
      script.onload = () => window.L?.map ? resolve(window.L) : reject(new Error("Leaflet no cargó"));
      script.onerror = () => reject(new Error("No se pudo cargar el mapa"));
      document.head.appendChild(script);
    }).catch(error => { leafletPromise = null; throw error; });
    return leafletPromise;
  }


  // Google Maps demo is isolated to this unpublished theme. Geoapify remains
  // responsible for address resolution, road distance, coverage and pricing.
  function ensureGoogleMaps() {
    if (window.google?.maps?.Map) return Promise.resolve(window.google.maps);
    if (googlePromise) return googlePromise;
    googlePromise = new Promise((resolve, reject) => {
      const callback = "__ad360GoogleDemoReady";
      let settled = false;
      const finish = (error) => {
        if (settled) return;
        settled = true;
        try { delete window[callback]; } catch (_) { window[callback] = undefined; }
        if (error) reject(error);
        else if (window.google?.maps?.Map) resolve(window.google.maps);
        else reject(new Error("Google Maps no terminó de cargar"));
      };
      window[callback] = () => finish();
      const script = document.createElement("script");
      script.async = true;
      script.src = "https://maps.googleapis.com/maps/api/js?key=" + encodeURIComponent(googleDemoKey) +
        "&loading=async&callback=" + callback + "&v=weekly";
      script.onerror = () => finish(new Error("No se pudo cargar Google Maps"));
      document.head.appendChild(script);
    }).catch(error => { googlePromise = null; throw error; });
    return googlePromise;
  }

  async function showGooglePreviewMap(pos) {
    const Google = await ensureGoogleMaps();
    mapBox.hidden = false;
    mapBox.classList.add("ad-google-preview-map");
    if (mapEngine !== "google" || !map) {
      mapBox.replaceChildren();
      const canvas = document.createElement("div");
      canvas.className = "ad-google-map-canvas";
      canvas.setAttribute("aria-label", "Mapa interactivo para elegir la ubicación del delivery");
      mapBox.appendChild(canvas);
      const pin = document.createElement("div");
      pin.className = "ad-google-center-pin";
      pin.setAttribute("aria-hidden", "true");
      pin.innerHTML = '<span class="ad-google-pin-icecream">🍦</span>';
      mapBox.appendChild(pin);
      const tip = document.createElement("div");
      tip.className = "ad-google-map-tip";
      tip.textContent = "Mueve el mapa para ubicar tu dirección";
      mapBox.appendChild(tip);

      map = new Google.Map(canvas, {
        center: pos, zoom: 16, mapId: "DEMO_MAP_ID",
        disableDefaultUI: true, zoomControl: true,
        mapTypeControl: false, fullscreenControl: false,
        streetViewControl: false, clickableIcons: true,
        gestureHandling: "greedy"
      });
      marker = null; // The heladito is fixed; the map moves beneath it.
      mapEngine = "google";
      map.addListener("dragend", () => {
        const c = map.getCenter();
        if (c) void relocatePin({ lat: c.lat(), lng: c.lng() });
      });
      map.addListener("click", event => {
        if (event.latLng) void relocatePin({ lat: event.latLng.lat(), lng: event.latLng.lng() });
      });
    } else {
      map.setCenter(pos);
    }
  }

  async function showMap(pos) {
    if (googleDemoKey) {
      try { await showGooglePreviewMap(pos); return; }
      catch (error) {
        console.warn("AD360: Google Maps demo no disponible, se conserva el mapa anterior.", error);
        mapBox.classList.remove("ad-google-preview-map");
        map = null;
        mapEngine = null;
      }
    }
    const L = await ensureLeaflet();
    mapBox.hidden = false;
    if (!map || mapEngine !== "leaflet") {
      mapBox.replaceChildren();
      map = L.map(mapBox, { zoomControl: true, scrollWheelZoom: false }).setView([pos.lat, pos.lng], 16);
      mapEngine = "leaflet";
      L.tileLayer("https://maps.geoapify.com/v1/tile/osm-bright/{z}/{x}/{y}.png?apiKey={apiKey}", {
        apiKey, maxZoom: 20,
        attribution: 'Powered by <a href="https://www.geoapify.com/" target="_blank" rel="noopener">Geoapify</a> | © <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a> contributors'
      }).addTo(map);
      const pin = L.divIcon({
        className: "ad-geo-pin",
        html: '<span style="display:grid;place-items:center;width:42px;height:42px;border:3px solid #fff;background:#fff6ee;border-radius:50%;box-shadow:0 5px 16px #29345255;font-size:23px">🍦</span>',
        iconSize: [45, 45], iconAnchor: [22, 22]
      });
      marker = L.marker([pos.lat, pos.lng], { draggable: true, icon: pin }).addTo(map);
      marker.on("dragend", () => {
        const p = marker.getLatLng();
        void relocatePin({ lat: p.lat, lng: p.lng });
      });
      map.on("click", e => void relocatePin({ lat: e.latlng.lat, lng: e.latlng.lng }));
    } else {
      map.setView([pos.lat, pos.lng], 16);
      marker.setLatLng([pos.lat, pos.lng]);
    }
    requestAnimationFrame(() => { if (mapEngine === "leaflet" && !mapBox.hidden) map.invalidateSize(); });
  }

  async function drivingDistance(origin, destination) {
    const data = await geoapify("routing", {
      waypoints: `${origin.lat},${origin.lng}|${destination.lat},${destination.lng}`,
      mode: "drive", type: "short", units: "metric"
    });
    const meters = Number(data?.features?.[0]?.properties?.distance);
    if (!Number.isFinite(meters) || meters < 0) throw new Error("Ruta no disponible");
    return meters;
  }

  function inApprovedPreliminaryCoverage(place, radiusMeters, origin) {
    if (!isInTrujilloRegion(place.pos) || (place.country && place.country !== "pe")) return false;
    // Huanchaco requires a separate verified service area; do not treat it as a normal circular zone.
    if (isRemoteSpecialZone(place)) return false;
    return !!radialTier(radiusMeters,origin);
  }

  async function assess(place) {
    if (!place?.pos || !Number.isFinite(place.pos.lat) || !Number.isFinite(place.pos.lng)) {
      message("Selecciona una dirección válida.", true); return;
    }
    invalidate();
    const current = run;
    selected = place;
    // Nunca mostrar 'Punto marcado en el mapa' como si fuera una dirección real.
    input.value = String(place.address || "").trim();
    streetInput.value = String(place.streetAddress || "").trim();
    streetConfirmation.style.display = streetInput.value ? "none" : "block";
    if(!streetInput.value)streetInput.placeholder = "Escribe la calle y número de entrega";
    hideSuggestions();
    changeButton.hidden = false;
    message("Revisando el punto y calculando las rutas de delivery...");
    status.textContent = "Puedes tocar el mapa o mover el marcador para ajustar la puerta de entrega.";
    try {
      await showMap(place.pos);
      if (current !== run) return;
      if (!isInTrujilloRegion(place.pos) || (place.country && place.country !== "pe")) {
        message("¡Todavía no llegamos a tu ubicación! Cambia la dirección o elige recojo en tienda.", true);
        return;
      }
      const routes = await Promise.allSettled(centers.map(async center => ({
        origin: center.name,
        meters: await drivingDistance(center.position, place.pos)
      })));
      if (current !== run) return;
      const options = routes.filter(x => x.status === "fulfilled").map(x => x.value).sort((a, b) => a.meters - b.meters);
      if (!options.length) throw new Error("No encontramos una ruta por carretera");
      // Choose the nearest Shopify location by straight-line radius for tariff matching.
      const nearestRadius = centers.map(center => ({
        origin: center.name, radiusMeters: haversine(center.position, place.pos)
      })).sort((a, b) => a.radiusMeters - b.radiusMeters)[0];
      if (!inApprovedPreliminaryCoverage(place, nearestRadius.radiusMeters,nearestRadius.origin)) {
        message(isRemoteSpecialZone(place)
          ? "Huanchaco: delivery referencial S/ 10, consumo mínimo S/ 45 en productos. Es un pedido especial sujeto a confirmar la dirección y disponibilidad; todavía no se puede pagar por delivery desde esta pantalla."
          : "Esta dirección está fuera de las zonas urbanas habilitadas. Las zonas lejanas requieren confirmación especial. Puedes cambiar la ubicación o elegir recojo en tienda.", true);
        return;
      }
      const road = options.find(x => x.origin === nearestRadius.origin);
      if (!road) throw new Error("No pudimos calcular la ruta desde la tienda más cercana");
      const tier = radialTier(nearestRadius.radiusMeters,nearestRadius.origin);
      quote = { ...road, radiusMeters: nearestRadius.radiusMeters, fee: tier.fee, minimum: minimumFor(place, nearestRadius.radiusMeters,nearestRadius.origin) };
      root.dataset.deliveryFee = String(quote.fee);
      message("Radio aproximado: " + (quote.radiusMeters / 1000).toFixed(2) + " km desde " + quote.origin + ". Recorrido por calles: " + (quote.meters / 1000).toFixed(2) + " km. Delivery normal " + fmtMoney(quote.fee) + ", gratis desde S/ 70 en productos. " + minimumLabel(quote.minimum) + "Confirma el marcador para continuar.");
      confirmButton.hidden = false;
    } catch (error) {
      if (current !== run) return;
      message("No pudimos verificar esta ubicación o su ruta. Inténtalo de nuevo o selecciona recojo.", true);
    } finally {
      if (current === run) renderCartNote();
    }
  }

  async function relocatePin(pos) {
    if (!Number.isFinite(pos?.lat) || !Number.isFinite(pos?.lng)) return;
    invalidate();
    const current = run;
    changeButton.hidden = false;
    message("Identificando la calle más cercana a tu ubicación...");
    if (marker && mapEngine === "leaflet") marker.setLatLng([pos.lat, pos.lng]);
    try {
      // Autocompletar dirección del marcador: primero Google, luego Geoapify.
      // Las coordenadas del pin jamás se sustituyen por las del resultado.
      let found=null;
      if(mapEngine==="google"&&window.google?.maps?.Geocoder){
        try{
          const response=await new window.google.maps.Geocoder().geocode({location:pos,language:"es"});
          if(current!==run)return;
          const candidates=(response.results||[]).map(row=>{
            const parts=row.address_components||[];
            const component=kind=>String(parts.find(x=>(x.types||[]).includes(kind))?.long_name||"").trim();
            const location=row.geometry?.location;
            const meters=location?haversine(pos,{lat:location.lat(),lng:location.lng()}):Infinity;
            const exact=(row.types||[]).some(x=>["street_address","premise","subpremise","intersection"].includes(x));
            const road=component("route");
            if(!road||meters>(exact?180:220))return null;
            const streetAddress=[road,component("street_number")].filter(Boolean).join(" ");
            const district=component("sublocality_level_1")||component("sublocality")||component("administrative_area_level_3");
            const city=component("locality")||"Trujillo",postcode=component("postal_code");
            return {address:[streetAddress,district,city,"La Libertad"].filter(Boolean).join(", "),
              streetAddress,district,city,postcode,country:"pe",pos,locality:[district,city].filter(Boolean).join(" "),meters,exact};
          }).filter(Boolean).sort((a,b)=>(Number(b.exact)-Number(a.exact))||(a.meters-b.meters));
          found=candidates[0]||null;
        }catch(error){console.warn("AD360: Google no reconoció una calle próxima al pin.",error);}
      }
      if(!found&&apiKey){
        try{
          for(const type of ["street",null]){
            const params={lat:pos.lat,lon:pos.lng,format:"json",lang:"es",limit:5};
            if(type)params.type=type;
            const response=await geoapify("geocode/reverse",params);
            if(current!==run)return;
            const options=(response.results||[]).map(row=>{
              const coords={lat:Number(row.lat),lng:Number(row.lon)};
              if(!Number.isFinite(coords.lat)||!Number.isFinite(coords.lng)||!String(row.street||"").trim())return null;
              const meters=haversine(pos,coords);
              // Evitar presentar vías alejadas, como Carretera Vista Alegre.
              if(meters>170)return null;
              const address=normalizeResult(row,pos);
              return address?{...address,meters}:null;
            }).filter(Boolean).sort((a,b)=>a.meters-b.meters);
            if(options.length){found=options[0];break;}
          }
        }catch(error){console.warn("AD360: Geoapify no reconoció una calle próxima al pin.",error);}
      }
      if(current!==run)return;
      // Si ningún proveedor reconoce una calle cercana, conservar el pin y
      // pedir confirmación manual sin mostrar un nombre genérico como dirección.
      if(!found)found={address:"",streetAddress:"",district:"",city:"Trujillo",postcode:"",country:"pe",pos};
      await assess({...found,pos});
      if(current!==run)return;
      if(found.streetAddress){
        status.textContent="Dirección encontrada automáticamente. Comprueba que sea tu calle y confirma la ubicación.";
      }else{
        status.textContent="Punto marcado. No pudimos reconocer la calle con precisión; escríbela en el campo de corrección.";
        message("Conservamos tu marcador. Escribe el nombre exacto de la calle para continuar.",true);
      }
    } catch (error) {
      if (current !== run) return;
      message("No pudimos identificar la calle de este punto. Mueve el mapa a la calle más cercana o escribe tu dirección.", true);
      status.textContent = "No confirmes una referencia cercana como si fuera tu domicilio.";
    }
  }

  async function searchAddresses(text) {
    if (!apiKey || text.length < 3) { hideSuggestions(); return; }
    const sequence = ++searchSequence;
    if (searchAbort) searchAbort.abort();
    searchAbort = new AbortController();
    try {
      const data = await geoapify("geocode/autocomplete", {
        text: text + (/(trujillo|huanchaco|per[uú])/i.test(text) ? "" : ", Trujillo, Perú"),
        format: "json", lang: "es", limit: 6,
        filter: "countrycode:pe", bias: "proximity:-79.03111,-8.11653"
      }, searchAbort.signal);
      if (sequence !== searchSequence) return;
      const results = (data?.results || []).map(normalizeResult).filter(x => x?.address);
      suggestions.replaceChildren();
      if (!results.length) {
        status.textContent = "No encontramos coincidencias. Intenta indicar calle y distrito, o utiliza tu GPS.";
        hideSuggestions(); return;
      }
      for (const result of results) {
        const option = document.createElement("button");
        option.type = "button";
        option.textContent = result.address;
        option.setAttribute("aria-label", "Seleccionar " + result.address);
        Object.assign(option.style, {
          display: "block", width: "100%", padding: "11px 12px", textAlign: "left",
          border: "0", background: "#ffffff", borderBottom: "1px solid #eee6e0",
          color: "#293452", cursor: "pointer", fontSize: "14px"
        });
        option.addEventListener("click", () => { void assess(result); });
        suggestions.appendChild(option);
      }
      suggestions.style.cssText = "display:block;position:relative;z-index:1002;background:white;box-shadow:0 7px 18px #0002;border-radius:12px;overflow:hidden;max-height:250px;overflow-y:auto";
      suggestions.hidden = false;
    } catch (error) {
      if (error?.name === "AbortError" || sequence !== searchSequence) return;
      hideSuggestions();
      status.textContent = "No pudimos buscar direcciones. Verifica tu conexión y la clave de Geoapify.";
    }
  }

  // Keep the visible Shopify input. Previous CSS hides it only if ad-maps-ready is set;
  // deliberately do not set that Google-specific class.
  if (mapMount) mapMount.replaceChildren();
  input.placeholder = "Calle, número y distrito en Trujillo";
  input.autocomplete = "off";
  input.setAttribute("aria-label", "Busca tu dirección de entrega");
  hideSuggestions();
  confirmButton.hidden = true;
  changeButton.hidden = true;

  if (!apiKey) {
    status.textContent = "Falta configurar Geoapify en el tema privado.";
    message("El delivery necesita una clave Geoapify. Mientras tanto, puedes elegir recojo en tienda.", true);
  } else {
    status.textContent = "Escribe tu dirección y elige una sugerencia, o usa la ubicación del teléfono.";
    message("Selecciona una dirección de entrega para comprobar la cobertura.");
  }

  input.addEventListener("input", () => {
    ++searchSequence;
    if (searchTimer) clearTimeout(searchTimer);
    if (searchAbort) searchAbort.abort();
    invalidate({ hideMap: false });
    hideSuggestions();
    message("Selecciona una dirección sugerida; no basta con escribirla.");
    const value = input.value.trim();
    if (value.length >= 3) searchTimer = setTimeout(() => { void searchAddresses(value); }, 400);
  });
  input.addEventListener("keydown", event => { if (event.key === "Escape") hideSuggestions(); });

  const geolocateButton = el("ad-geolocate");
  if (geolocateButton) geolocateButton.onclick = () => {
    if (!apiKey || !navigator.geolocation) {
      message("Ubicación no disponible. Busca tu dirección escribiéndola.", true); return;
    }
    status.textContent = "Obteniendo tu ubicación...";
    navigator.geolocation.getCurrentPosition(
      async p => {
        const pos = { lat: p.coords.latitude, lng: p.coords.longitude };
        status.textContent = "La ubicación GPS es aproximada. Comprueba el punto exacto en el mapa.";
        await relocatePin(pos);
      },
      () => { message("No se pudo acceder al GPS. Puedes buscar tu dirección manualmente.", true); },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 }
    );
  };

  // When the customer opens Delivery, show a map immediately and request GPS once.
  // Browser geolocation always requires the customer's permission when not previously granted.
  let requestedAutomaticGps = false;
  function activateDeliveryMap() {
    const screen = root.querySelector('[data-screen="delivery"]');
    if ((!apiKey && !googleDemoKey) || !isDelivery() || !screen?.classList.contains("is-active")) return;
    void showMap(selected?.pos || cityCenter).then(() => {
      if (mapEngine === "leaflet") map?.invalidateSize();
      if (!isDelivery()) return;
      if (!requestedAutomaticGps && !selected) {
        requestedAutomaticGps = true;
        geolocateButton?.click();
      }
    }).catch(() => message("No se pudo mostrar el mapa. Busca tu dirección manualmente.", true));
  }
  root.querySelectorAll('[data-go="delivery"],[data-nav="delivery"]').forEach(button => {
    button.addEventListener("click", () => requestAnimationFrame(activateDeliveryMap));
  });

  confirmButton.onclick = () => {
    if (!selected || !quote || !inApprovedPreliminaryCoverage(selected, quote.radiusMeters,quote.origin)) return;
    const exactStreet=streetInput.value.trim();
    if(exactStreet.length<5 || exactStreet==="Punto marcado en el mapa"){
      streetInput.focus();
      alert("Escribe o confirma la calle exacta de entrega. No enviaremos una carretera cercana por error.");
      return;
    }
    selected={...selected,streetAddress:exactStreet,address:[exactStreet,selected.district,selected.city,"La Libertad"].filter(Boolean).join(", ")};
    input.value=selected.address;
    confirmed = true;
    setValidated(true);
    confirmButton.hidden = true;
    changeButton.hidden = false;
    message("¡Estamos en tu ubicación! Delivery normal: " + fmtMoney(quote.fee) + "; gratis desde S/ 70 en productos. " + minimumLabel(quote.minimum) + "Puedes continuar al menú.");
    status.textContent = "Ubicación confirmada: " + selected.address;
    renderCartNote();
    // The existing menu transition also stores the selected address for cart line items.
    el("ad-start-menu")?.click();
  };

  changeButton.onclick = () => {
    invalidate({ clearAddress: true, hideMap: false });
    hideSuggestions();
    message("Busca una nueva dirección o usa tu ubicación actual.");
    input.focus();
  };

  let previousMode = isDelivery();
  root.querySelectorAll("[data-method]").forEach(button => button.addEventListener("click", () => {
    const nextMode = isDelivery();
    if (previousMode !== nextMode) invalidate({ hideMap: true });
    previousMode = nextMode;
    renderCartNote();
    if (nextMode) requestAnimationFrame(activateDeliveryMap);
  }));
  root.querySelectorAll('input[name="ad-store"]').forEach(button => button.addEventListener("change", renderCartNote));
  el("ad-start-menu")?.addEventListener("click", e => {
    if (isDelivery() && (!apiKey || !confirmed || !quote || !selected)) {
      e.preventDefault(); e.stopImmediatePropagation();
      message("Para ver el menú con delivery, confirma antes una dirección dentro de nuestra cobertura.", true);
    }
  }, true);
  el("ad-bag")?.addEventListener("click", renderCartNote);
  // El carrito principal llena el subtotal después de abrirse: esperar ese
  // dato evita mensajes erróneos de "te faltan S/ 70" con un pedido de S/ 16.
  const cartSubtotalElement=el("ad-cart-subtotal");
  if(cartSubtotalElement){
    new MutationObserver(()=>renderCartNote()).observe(cartSubtotalElement,{childList:true,characterData:true,subtree:true});
  }
  root.querySelectorAll('[data-nav="cart"]').forEach(button => button.addEventListener("click", renderCartNote));


  const ad360PickupLocations = {"Larco":"gid://shopify/Location/78600503572","Húsares":"gid://shopify/Location/78601781524"};
  const ad360PickupMutation = 'mutation AD360CartForPickup($input:CartInput!){cartCreate(input:$input){cart{checkoutUrl totalQuantity buyerIdentity{preferences{delivery{deliveryMethod pickupHandle}}}lines(first:250){nodes{quantity merchandise{...on ProductVariant{id}}}}}userErrors{message code}}}';
  function ad360StorefrontAttributes(input) {
    return Object.entries(input || {}).filter(([key,value])=>key && value != null && typeof value !== "object").map(([key,value])=>({key:String(key),value:String(value)}));
  }


  // Usar la tarifa ofrecida por Shopify para ESTA dirección y ESTE carrito.
  // Es una consulta independiente: su fallo nunca interrumpe el checkout real.
  // La tarifa local no viene del perfil de envíos: depende de la zona de cada local.
  // Exactamente la misma dirección va al cotizador y al checkout.
  // No inventar número ni distrito cuando el geocodificador no los conoce.
  function ad360ShopifyDeliveryAddress(place){
    const street=String(place?.streetAddress||"").trim();
    if(street.length<5)throw new Error("Confirma la calle antes de continuar.");
    const address={
      address1:street.slice(0,255),
      city:String(place?.city||"Trujillo").trim().slice(0,90),
      provinceCode:"PE-LAL",countryCode:"PE"
    };
    if(place?.district)address.address2=String(place.district).trim().slice(0,100);
    if(/^[0-9]{5}$/.test(String(place?.postcode||"").trim()))address.zip=String(place.postcode).trim();
    return address;
  }
  const ad360RateQuoteMutation='mutation AD360LocalRate($input:CartInput!){cartCreate(input:$input){cart{deliveryGroups(first:10){nodes{deliveryOptions{deliveryMethodType estimatedCost{amount currencyCode}}}}}userErrors{message}}}';
  const ad360RateQuoteCache=new Map();
  // La consulta de Shopify es el precio autorizado que mostramos al comprador.
  // Cuando Shopify no devuelve tarifa LOCAL nunca mostrar la cifra del radio como final.
  function ad360FetchLocalDeliveryRate(cart,place){
    const key=JSON.stringify([cart.token,cart.items_subtotal_price,cart.items.map(i=>[i.variant_id,i.quantity,i.properties]),place.streetAddress,place.city,place.postcode,place.pos]);
    if(ad360RateQuoteCache.has(key))return ad360RateQuoteCache.get(key);
    const task=(async()=>{
      if(!place?.streetAddress||!Array.isArray(cart.items)||!cart.items.length)return null;
      const lines=cart.items.map(item=>({
        merchandiseId:"gid://shopify/ProductVariant/"+Number(item.variant_id),
        quantity:Number(item.quantity),
        attributes:ad360StorefrontAttributes(item.properties)
      }));
      if(lines.some(l=>!Number.isSafeInteger(l.quantity)||l.quantity<1||!/ProductVariant\/[1-9][0-9]*$/.test(l.merchandiseId)))return null;
      const shippingAddress=ad360ShopifyDeliveryAddress(place);
      const input={
        lines,
        buyerIdentity:{countryCode:"PE",preferences:{delivery:{deliveryMethod:["SHIPPING"]}}},
        delivery:{addresses:[{selected:true,oneTimeUse:true,address:{deliveryAddress:shippingAddress}}]}
      };
      const codes=(Array.isArray(cart.discount_codes)?cart.discount_codes:[]).map(d=>d?.code).filter(Boolean);
      if(codes.length)input.discountCodes=codes;
      const headers={"Content-Type":"application/json"};
      const token=(root.dataset.storefrontPickupToken||"").trim();
      if(token)headers["X-Shopify-Storefront-Access-Token"]=token;
      const controller=new AbortController(),timer=setTimeout(()=>controller.abort(),10000);
      let response;
      try{
        response=await fetch("/api/2026-10/graphql.json",{
          method:"POST",headers,credentials:"omit",signal:controller.signal,
          body:JSON.stringify({query:ad360RateQuoteMutation,variables:{input}})
        });
      }finally{clearTimeout(timer);}
      if(!response.ok)return null;
      const data=await response.json();
      if(data.errors?.length||data.data?.cartCreate?.userErrors?.length)return null;
      const groups=data.data?.cartCreate?.cart?.deliveryGroups?.nodes||[];
      if(groups.length!==1)return null;
      const localRates=(groups[0].deliveryOptions||[])
        .filter(option=>option.deliveryMethodType==="LOCAL"&&option.estimatedCost?.currencyCode==="PEN")
        .map(option=>Number(option.estimatedCost.amount))
        .filter(price=>Number.isFinite(price)&&price>=0);
      if(!localRates.length)return null;
      // Shopify aplica la opción local de menor precio cuando varias zonas califican.
      return Math.round(Math.min(...localRates)*100);
    })().catch(error=>{console.warn("AD360: no se pudo cotizar la entrega local desde Shopify.",error);return null;});
    if(ad360RateQuoteCache.size>6)ad360RateQuoteCache.clear();
    ad360RateQuoteCache.set(key,task);
    return task;
  }

  // Delivery uses a NEW Storefront cart with the selected address; /checkout reused
  // the old pickup context, while the Google/Geoapify pin lived only in cart notes.
  const ad360ShippingMutation='mutation AD360CartForShipping($input:CartInput!){cartCreate(input:$input){cart{checkoutUrl totalQuantity buyerIdentity{preferences{delivery{deliveryMethod}}}delivery{addresses(selected:true){selected address{...on CartDeliveryAddress{address1 address2 city zip provinceCode countryCode}}}}deliveryGroups(first:10){nodes{deliveryOptions{deliveryMethodType estimatedCost{amount currencyCode}}}}lines(first:250){nodes{quantity merchandise{...on ProductVariant{id}}}}}userErrors{code message}}}';
  async function ad360ContinueWithPrefilledDelivery(cart,place,expectedLocalFeeCents){
    if(!place?.pos || !Number.isFinite(place.pos.lat) || !Number.isFinite(place.pos.lng))
      throw new Error("Confirma la ubicación de entrega antes de pagar.");
    const street=String(place.streetAddress||place.address||"").trim();
    if(street.length<6)throw new Error("La calle seleccionada está incompleta. Ajusta el marcador o busca una dirección.");
    const lines=(cart.items||[]).map(item=>{
      const variantId=Number(item.variant_id),qty=Number(item.quantity);
      if(!Number.isSafeInteger(variantId)||variantId<=0||!Number.isSafeInteger(qty)||qty<=0)
        throw new Error("No se pudo validar el producto del carrito.");
      const line={merchandiseId:"gid://shopify/ProductVariant/"+variantId,quantity:qty,attributes:ad360StorefrontAttributes(item.properties)};
      if(item.selling_plan_allocation?.selling_plan?.id)
        line.sellingPlanId="gid://shopify/SellingPlan/"+String(item.selling_plan_allocation.selling_plan.id).split("/").pop();
      return line;
    });
    if(!lines.length||lines.length>250)throw new Error("Carrito vacío o demasiado grande.");
    const deliveryAddress=ad360ShopifyDeliveryAddress(place);
    const attrs=ad360StorefrontAttributes(cart.attributes).filter(a=>a.key!=="AD360_HIDE_DELIVERY_WHEN_PICKUP"&&a.key!=="AD360_PICKUP_BRANCH");
    const input={
      lines,attributes:attrs,note:String(cart.note||""),
      buyerIdentity:{countryCode:"PE",preferences:{delivery:{deliveryMethod:["SHIPPING"]}}},
      delivery:{addresses:[{selected:true,oneTimeUse:true,address:{deliveryAddress}}]}
    };
    const codes=(Array.isArray(cart.discount_codes)?cart.discount_codes:[]).map(x=>String(x?.code||"").trim()).filter(Boolean);
    if(codes.length)input.discountCodes=codes;
    const controller=new AbortController(),timer=setTimeout(()=>controller.abort(),12000);
    const headers={"Content-Type":"application/json"};
    const token=(root.dataset.storefrontPickupToken||"").trim();
    if(token)headers["X-Shopify-Storefront-Access-Token"]=token;
    let response;
    try{response=await fetch("/api/2026-10/graphql.json",{method:"POST",headers,credentials:"omit",signal:controller.signal,body:JSON.stringify({query:ad360ShippingMutation,variables:{input}})});}
    finally{clearTimeout(timer);}
    if(!response.ok)throw new Error("Shopify no pudo preparar el checkout de delivery.");
    const data=await response.json();
    if(data.errors?.length||data.data?.cartCreate?.userErrors?.length){
      console.warn("AD360 delivery cartCreate:",data.errors||data.data?.cartCreate?.userErrors);
      throw new Error("Shopify no aceptó la dirección. Confirma la calle o el número y vuelve a intentar.");
    }
    const prepared=data.data?.cartCreate?.cart;
    if(!prepared?.checkoutUrl||Number(prepared.totalQuantity)!==Number(cart.item_count))
      throw new Error("Shopify no confirmó los productos para delivery.");
    if(!prepared.buyerIdentity?.preferences?.delivery?.deliveryMethod?.includes("SHIPPING"))
      throw new Error("No se pudo preseleccionar Envío en Shopify.");
    // No entrar a un checkout de Shopify que no ofrezca entrega local al precio anunciado.
    // Cart.deliveryGroups usa DeliveryMethodType.LOCAL para entrega local.
    const groups=prepared.deliveryGroups?.nodes||[];
    const localCosts=groups.length===1?(groups[0].deliveryOptions||[])
      .filter(option=>option.deliveryMethodType==="LOCAL"&&option.estimatedCost?.currencyCode==="PEN")
      .map(option=>Math.round(Number(option.estimatedCost.amount)*100))
      .filter(Number.isFinite):[];
    if(!Number.isInteger(expectedLocalFeeCents)||expectedLocalFeeCents<0||!localCosts.includes(expectedLocalFeeCents)){
      throw new Error("Shopify no confirmó la misma tarifa de entrega local que muestra el carrito. Comprueba la dirección y vuelve a intentar.");
    }
    const verified=(prepared.delivery?.addresses||[]).find(a=>a.selected&&a.address?.countryCode==="PE");
    if(!verified?.address?.address1 || verified.address.address1.trim()!==deliveryAddress.address1.trim())
      throw new Error("Shopify no recibió correctamente la calle seleccionada.");
    if(verified.address.city?.trim()!==deliveryAddress.city.trim())
      throw new Error("Shopify no confirmó la ciudad de entrega.");
    if(deliveryAddress.zip && verified.address.zip!==deliveryAddress.zip)
      throw new Error("Shopify no confirmó el código postal de entrega.");
    const expected=new Map(),actual=new Map();
    for(const line of lines)expected.set(line.merchandiseId,(expected.get(line.merchandiseId)||0)+line.quantity);
    for(const line of prepared.lines?.nodes||[])actual.set(line.merchandise?.id,(actual.get(line.merchandise?.id)||0)+Number(line.quantity||0));
    if(actual.size!==expected.size||[...expected].some(([key,qty])=>actual.get(key)!==qty))
      throw new Error("Los productos del checkout no coinciden con el carrito.");
    const next=new URL(prepared.checkoutUrl);
    if(next.protocol!=="https:"||!["aldodiego.pe","aldodiego.myshopify.com","checkout.shopify.com"].includes(next.hostname))
      throw new Error("Shopify devolvió un destino de pago no autorizado.");
    window.location.assign(next.href);
  }

  async function ad360ContinueWithPrefilledPickup(cart,store) {
    const locationId=ad360PickupLocations[store];
    const token=(root.dataset.storefrontPickupToken || "").trim();
    if(!locationId) throw new Error("El retiro automático todavía no está habilitado para esta tienda.");
    const items=cart.items || [];
    if(!items.length || items.length>250) throw new Error("El carrito tiene una cantidad de productos no compatible.");
    const lines=items.map(item=>{
      const variantId=Number(item.variant_id), quantity=Number(item.quantity);
      if(!Number.isSafeInteger(variantId)||variantId<=0||!Number.isSafeInteger(quantity)||quantity<=0)
        throw new Error("No se pudo validar la cantidad de un producto.");
      const line={merchandiseId:"gid://shopify/ProductVariant/"+variantId,quantity,attributes:ad360StorefrontAttributes(item.properties)};
      if(item.selling_plan_allocation?.selling_plan?.id)
        line.sellingPlanId="gid://shopify/SellingPlan/"+String(item.selling_plan_allocation.selling_plan.id).split("/").pop();
      return line;
    });
    // App de reglas de entrega: marcar SOLO el checkout nativo con preferencia de retiro.
    // No modificar /cart.js ni los pedidos de delivery. La regla de Shipy debe leer
    // el atributo AD360_HIDE_DELIVERY_WHEN_PICKUP = YES, nunca ocultar por defecto.
    const checkoutAttributes=ad360StorefrontAttributes(cart.attributes)
      .filter(attribute=>attribute.key!=="AD360_HIDE_DELIVERY_WHEN_PICKUP"&&attribute.key!=="AD360_PICKUP_BRANCH");
    checkoutAttributes.push(
      {key:"AD360_HIDE_DELIVERY_WHEN_PICKUP",value:"YES"},
      {key:"AD360_PICKUP_BRANCH",value:store}
    );
    const input={
      lines,
      attributes:checkoutAttributes,
      note:String(cart.note||""),
      buyerIdentity:{countryCode:"PE",preferences:{delivery:{deliveryMethod:["PICK_UP"],pickupHandle:[locationId]}}}
    };
    const discounts=(Array.isArray(cart.discount_codes)?cart.discount_codes:[]).map(x=>String(x?.code||"").trim()).filter(Boolean);
    if(discounts.length)input.discountCodes=discounts;
    const controller=new AbortController(),timeout=setTimeout(()=>controller.abort(),12000);
    const headers={"Content-Type":"application/json"};
    if(token)headers["X-Shopify-Storefront-Access-Token"]=token;
    let response;
    try{
      response=await fetch("/api/2026-10/graphql.json",{
        method:"POST",
        headers,
        credentials:"omit", signal:controller.signal,
        body:JSON.stringify({query:ad360PickupMutation,variables:{input}})
      });
    }finally{clearTimeout(timeout);}
    if(!response.ok)throw new Error("Shopify no pudo preparar el carrito con retiro. Tu pedido permanece intacto.");
    const result=await response.json();
    if(result.errors?.length || result.data?.cartCreate?.userErrors?.length)
      throw new Error("Shopify no pudo registrar la preferencia de retiro. Reintenta antes de pagar.");
    const prepared=result.data?.cartCreate?.cart;
    if(!prepared?.checkoutUrl || Number(prepared.totalQuantity)!==Number(cart.item_count))
      throw new Error("Shopify no confirmó todos los productos para retiro.");
    const prefs=prepared.buyerIdentity?.preferences?.delivery;
    if(!prefs?.deliveryMethod?.includes("PICK_UP")||!prefs?.pickupHandle?.includes(locationId))
      throw new Error("Shopify no confirmó el retiro en "+store+".");
    const expected=new Map();
    for(const line of lines)expected.set(line.merchandiseId,(expected.get(line.merchandiseId)||0)+line.quantity);
    const actual=new Map();
    for(const line of prepared.lines?.nodes||[]){
      const id=String(line.merchandise?.id||"");
      actual.set(id,(actual.get(id)||0)+Number(line.quantity||0));
    }
    if(actual.size!==expected.size || [...expected].some(([id,quantity])=>actual.get(id)!==quantity))
      throw new Error("El carrito del checkout no coincide con los productos solicitados.");
    const target=new URL(prepared.checkoutUrl);
    if(target.protocol!=="https:" || !(target.hostname==="aldodiego.pe"||target.hostname==="aldodiego.myshopify.com"||target.hostname==="checkout.shopify.com"))
      throw new Error("Shopify devolvió un destino de pago no autorizado.");
    window.location.assign(target.href);
  }

  function confirmPickupInCheckout(store) {
    const dialog = el("ad360-pickup-native-dialog");
    if (!dialog || typeof dialog.showModal !== "function") {
      return Promise.resolve(window.confirm("Recogerás en " + store + ". Al pagar en Shopify selecciona Entrega > Retiro > " + store + ". Si ves una tarifa de delivery, selecciona Retiro antes de pagar. ¿Continuar?"));
    }
    const storeLabel = el("ad360-pickup-dialog-store");
    if (storeLabel) storeLabel.textContent = store;
    dialog.returnValue = "cancel";
    return new Promise(resolve => {
      dialog.addEventListener("close", () => resolve(dialog.returnValue === "proceed"), { once:true });
      dialog.showModal();
    });
  }
  const checkoutButton = el("ad-checkout");
  if (checkoutButton) checkoutButton.onclick = async () => {
    const mode = isDelivery() ? "Delivery" : "Recojo en tienda";
    const store = root.querySelector('input[name="ad-store"]:checked')?.value || "Larco";
    if (isDelivery() && (!confirmed || !quote || !selected)) {
      alert("Confirma tu dirección y cobertura antes de pagar."); return;
    }
     checkoutButton.disabled = true;
    try {
      const response = await fetch("/cart.js", { credentials: "same-origin" });
      if (!response.ok) throw new Error("Carrito no disponible");
      const cart = await response.json();
      if (!cart.items?.length) throw new Error("El carrito está vacío");
      const incompatible = cart.items.some(item => {
        const itemMethod = item.properties?.["Método de entrega"];
        return itemMethod && itemMethod !== mode;
      });
      if (incompatible) {
        alert("Hay productos de otra modalidad de entrega. Retíralos y vuelve a agregarlos para continuar.");
        return;
      }
      if (isDelivery()) {
        const minimum = Number(quote.minimum) || 0;
        const subtotalCents = Number(cart.items_subtotal_price ?? cart.total_price);
        if (!Number.isFinite(subtotalCents)) throw new Error("No se pudo comprobar el subtotal");
        if (subtotalCents < minimum * 100) {
          const missing = (minimum * 100 - subtotalCents) / 100;
          alert("Para tu ubicación, la compra mínima en productos es " + fmtMoney(minimum) + ". Agrega " + fmtMoney(missing) + " más para habilitar delivery.");
          return;
        }
        // Permitir comprobar TODAS las zonas preliminares solo desde la URL
        // de vista previa de este tema NO PUBLICADO. La tarifa de Shopify
        // sigue siendo la autoridad final; nunca publicar esta excepción.
        // En vistas previas Shopify suele quitar ?preview_theme_id al navegar.
        // Este archivo existe únicamente en el tema inédito AD360 Reconstrucción.
        // No depender del parámetro perdido; mantener las verificaciones de cobertura.
        const safeDeliveryPreview=selected && inApprovedPreliminaryCoverage(selected,quote.radiusMeters,quote.origin);
        if(!checkoutReady && !safeDeliveryPreview){
          alert("El pago de delivery todavía está en pruebas. Abre la vista previa de Aldodiego 360 para comprobar las zonas; las tarifas definitivas se confirmarán en Shopify.");
          return;
        }
      }
      const attributes = {
        "AD360 método": mode,
        "AD360 tienda recojo": isDelivery() ? "" : store,
        "AD360 dirección": isDelivery() ? selected.address : "",
        "AD360 latitud": isDelivery() ? selected.pos.lat.toFixed(6) : "",
        "AD360 longitud": isDelivery() ? selected.pos.lng.toFixed(6) : "",
        "AD360 origen estimado": isDelivery() ? quote.origin : "",
        "AD360 distancia vial km": isDelivery() ? (quote.meters / 1000).toFixed(3) : "",
        "AD360 radio estimado km": isDelivery() ? (quote.radiusMeters / 1000).toFixed(3) : "",
        "AD360 delivery estimado PEN": isDelivery() ? String(effectiveFeeFor(Number(cart.items_subtotal_price ?? cart.total_price), quote)) : "",
        "AD360 delivery tarifa normal PEN": isDelivery() ? String(quote.fee) : "",
        "AD360 delivery gratis desde PEN": isDelivery() ? "70" : "",
        "AD360 compra mínima PEN": isDelivery() ? String(quote.minimum) : ""
      };
      const customerNote = (el("ad-cart-note")?.value || "").trim();
      const shippingNote = isDelivery()
        ? "Delivery: " + selected.address + " (origen estimado: " + quote.origin + ")"
        : "Recojo en tienda: " + store;
      const saved = await fetch("/cart/update.js", {
        method: "POST", credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ attributes, note: customerNote + (customerNote ? " | " : "") + shippingNote })
      });
      if (!saved.ok) throw new Error("No se guardó la modalidad de entrega");
      if (isDelivery()) {
        const last=await fetch("/cart.js",{credentials:"same-origin"});
        if(!last.ok)throw new Error("No se pudo volver a comprobar el carrito.");
        const latestCart=await last.json();
        const localFeeCents=await ad360FetchLocalDeliveryRate(latestCart,selected);
        if(localFeeCents===null){
          throw new Error("Shopify no ofrece delivery para esta dirección y el importe actual. En la zona de Calle las Acacias, una compra de S/ 16 no alcanza el mínimo; revisa calle, número y pedido mínimo.");
        }
        await ad360ContinueWithPrefilledDelivery(latestCart,selected,localFeeCents);
        return;
      }
      if (!isDelivery()) {
        if (store==="Larco" || store==="Húsares") {
          try {
            const latest=await fetch("/cart.js",{credentials:"same-origin"});
            if (!latest.ok) throw new Error("No pudimos comprobar los datos finales del pedido.");
            const confirmedCart=await latest.json();
            await ad360ContinueWithPrefilledPickup(confirmedCart,store);
            return;
          } catch (pickupError) {
            // Evitar el /checkout tradicional: podría abrir Envío aunque se eligió recojo.
            console.warn("AD360: no se pudo conservar el retiro preseleccionado.", pickupError);
            throw new Error("No pudimos abrir el pago con Retiro en " + store + " preseleccionado. Reintenta sin cambiar tu pedido.");
          }
        }
        if (!(await confirmPickupInCheckout(store))) return;
      }
      window.location.assign("/checkout");
    } catch (error) {
      alert("No pudimos preparar el pago: " + error.message);
    } finally {
      checkoutButton.disabled = false;
    }
  };

  renderCartNote();
})();